import { Box, Button, Stack, TextField, Typography, theme } from '@dtx-company/design-system/src'
import { FC, useEffect, useState } from 'react'
import { Formik } from 'formik'
import { Routes } from '@dtx-company/true-common/src/constants/routes'
import { SSORedirect } from '@dtx-company/inter-app/src/components/singleSignOn/SSORedirect'
import { logger } from '@dtx-company/logger'
import { triggerResetPassword } from '@dtx-company/ithaca-sdk/src'
import { usePrelogin } from '@dtx-company/inter-app/src/hooks/auth/usePrelogin'
import { useRouter } from 'next/router'
import Trans from 'next-translate/Trans'
import isEmail from 'validator/lib/isEmail'
import useTranslation from 'next-translate/useTranslation'

interface ForgotPasswordFormProps {
  returnLink?: string
  onReturn?: () => void
}

export const ForgotPasswordForm: FC<ForgotPasswordFormProps> = ({
  returnLink,
  onReturn
}): JSX.Element => {
  const [error, setError] = useState('')
  const [isConfirmationSent, setIsConfirmationSent] = useState(false)
  const { emailIsSSOEnabled, checkIfEmailSSOEnabled, startSSORedirect, ssoRedirectHtml } =
    usePrelogin()
  const { t } = useTranslation('auth')
  const router = useRouter()

  const initialEmail = (router.query?.email as string) || ''

  useEffect(() => {
    if (router.query?.email && router.isReady) {
      const { email: _email, ...restQuery } = router.query
      router.replace(
        {
          pathname: router.pathname,
          query: restQuery
        },
        undefined,
        { shallow: true }
      )
    }
  }, [router])

  return (
    <>
      <SSORedirect redirectFormHtml={ssoRedirectHtml} />
      <Formik
        initialValues={{ email: initialEmail }}
        validate={values => {
          const errors: { email?: string } = {}
          if (!values.email) {
            errors.email = 'Please enter your email'
          } else if (!isEmail(values.email)) {
            errors.email = 'Invalid email address'
          }
          return errors
        }}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            setError('')
            setSubmitting(true)
            if (emailIsSSOEnabled) {
              await startSSORedirect()
              return
            } else {
              await triggerResetPassword({ email: values.email })
              setIsConfirmationSent(true)
            }
          } catch (e) {
            setError(e.message)
            logger.logError(e, { technicalArea: 'authentication' })
          } finally {
            setSubmitting(false)
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          isValid
        }) => (
          <form onSubmit={handleSubmit}>
            <Box component={'section'}>
              <Box
                sx={{
                  maxWidth: 400,
                  margin: '0 auto',
                  padding: {
                    xs: 0,
                    sm: 8.5,
                    md: 'unset'
                  },
                  height: ['50vh', 'unset', 'unset']
                }}
              >
                {!isConfirmationSent && (
                  <Stack spacing={4} justifyContent="center">
                    <TextField
                      type="email"
                      autoComplete="email"
                      onChange={e => {
                        const email = e.target.value?.toLowerCase()
                        checkIfEmailSSOEnabled(email)
                        handleChange(e)
                      }}
                      onBlur={handleBlur}
                      value={values.email}
                      label={t('ResetPassword.inputLabel')}
                      labelAbove
                      error={!!errors.email && touched.email}
                      helperText={errors.email && touched.email ? errors.email : ''}
                      inputProps={{
                        name: 'email'
                      }}
                      data-testid="email-input"
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={!values.email || isSubmitting || !isValid}
                      loading={isSubmitting}
                      type="submit"
                      data-testid="submit-btn"
                    >
                      {t('ResetPassword.SendResetLink.Submit')}
                    </Button>
                    {error && (
                      <Typography
                        variant="bodySmall"
                        color={theme.palette.error.main}
                        style={{
                          fontWeight: 'bold',
                          textAlign: 'center',
                          marginTop: theme.spacing(3),
                          marginBottom: theme.spacing(3)
                        }}
                      >
                        {error}
                      </Typography>
                    )}
                  </Stack>
                )}
                {isConfirmationSent && (
                  <>
                    <Typography
                      component="p"
                      style={{
                        marginBottom: '20px'
                      }}
                      variant="headlineSmall"
                    >
                      {t('ResetPassword.ConfirmationSent.Title')}
                    </Typography>
                    <Typography
                      component="p"
                      variant="bodySmall"
                      style={{
                        marginBottom: 20
                      }}
                    >
                      <Trans
                        i18nKey="auth:ResetPassword.ConfirmationSent.Description"
                        values={{ email: values.email }}
                        components={[<strong key="email" />]}
                      />
                    </Typography>
                    <Typography component="p" variant="bodySmall">
                      {t('ResetPassword.ConfirmationSent.Addendum')}
                    </Typography>
                  </>
                )}
                <Box
                  sx={{
                    marginTop: '30px',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: isConfirmationSent ? 'left' : 'center',
                    width: isConfirmationSent ? '50%' : '100%'
                  }}
                >
                  <Button
                    href={returnLink || Routes.SIGN_IN}
                    variant="text"
                    color="primary"
                    onClick={e => {
                      if (onReturn) {
                        e.preventDefault()
                        onReturn()
                      }
                    }}
                  >
                    {t('ResetPassword.ReturnToSignIn')}
                  </Button>
                </Box>
              </Box>
            </Box>
          </form>
        )}
      </Formik>
    </>
  )
}
